/**
 * The forms step's behavior half: the studio's next required form in the held
 * flow, its autosave and submit, and Back. Draws nothing.
 */
import { useCallback, useMemo } from 'react';
import type { WebAppFlowContextResponse } from '@/api/types';
import type { FormResponseInput } from '@/types/forms';

import { usePieceEmit, usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { useFormPage, type FormPage } from '../../hooks/useWebForms';
import { contract, type FormsStepActions, type FormsStepEvents, type FormsStepState } from './contract';

export interface FormsStepInput extends PieceInput<FormsStepEvents> {
  readonly flowToken: string;
  readonly flowContext: WebAppFlowContextResponse;
  readonly offering: { readonly name: string; readonly path: string } | null;
}

export type FormsStepRuntime = PieceRuntime<typeof contract, FormsStepState, FormsStepActions> & {
  /** The form itself and its submit, for the standard drawing. */
  readonly page: FormPage;
  readonly submit: (responses: FormResponseInput[]) => void;
};

export function useFormsStep(input: FormsStepInput): FormsStepRuntime {
  const options = usePieceOptions(contract, input.options);
  const emit = usePieceEmit(input.onEvent);
  const page = useFormPage(input.flowToken, input.flowContext, input.offering);

  const submit = useCallback(
    (responses: FormResponseInput[]) => {
      const index = page.index;
      void page.submit(responses).then((ok) => {
        if (ok) emit('formSubmitted', { index });
      });
    },
    [page, emit],
  );
  const actions = useMemo<FormsStepActions>(() => ({ back: page.back }), [page.back]);

  return {
    state: { status: page.failed ? 'failed' : page.form ? 'ready' : 'loading', index: page.index, total: page.total },
    actions,
    options,
    page,
    submit,
  };
}
